export const BASE_NAME = { galilee: "Galilee area", dead_sea: "Dead Sea Resort", jerusalem: "Jerusalem" };
export const TYPE_LABEL = { travel: "Travel", touring: "Touring", resort: "Resort stay", departure: "Departure", arrival: "Arrival", transfer: "Transfer", return: "Return" };
export const BASIS = { biblical: "Biblical reference", tradition: "Traditional association", archaeology: "Archaeological site" };
export const FLAG_LABEL = { stairs: "Stairs", uneven: "Uneven terrain", long_walk: "Longer walk", water: "Water-based", narrow: "Narrow passages", west_bank: "West Bank location" };
export const STATUS_LABEL = { available: "Available for requests", requires_review: "Requires review", unavailable: "Temporarily unavailable", hidden: "Hidden" };
export const FEE_NOTE = "Some optional experiences may involve an additional cost. Our team will confirm pricing in your personalized proposal.";
export const ROUTING_NOTE = "Routing review: in the 11-day plan, Beit Shean follows the Dead Sea resort stay and another Dead Sea excursion (Masada, Qumran, Dead Sea Swim) comes later. Our team will review this sequence; your requested order is kept unless an administrator approves a change.";
export const rid = () => Math.random().toString(36).slice(2, 10);
export const siteMap = (cat) => Object.fromEntries(cat.sites.map((s) => [s.id, s]));

const nightBase = (n, t) => { const k = n - 1; return k <= t.galilee ? "galilee" : k <= t.galilee + t.dead_sea ? "dead_sea" : "jerusalem"; };
const place = (d, n, total, t) => { const last = n === total; const b = n === 1 || last ? null : nightBase(n, t); return { ...d, n, base: b, overnight: n === 1 ? "In flight (overnight)" : last ? "Home" : BASE_NAME[b] }; };
const siteItem = (id) => ({ id: rid(), type: "site", site_id: id, priority: null });
export const defaultItems = (d, site) => d.defaults.filter((id) => site[id]).map(siteItem);

export const specsFor = (total, cat) => { const p = cat.plan.map((x) => ({ ...x })); if (total === 11) p.splice(cat.resort_after, 0, { ...cat.resort_day }); return p; };
export const templateFor = (total, cat) => cat.templates.find((x) => x.days === total);

export function buildDays(total, cat, mode) {
  const t = templateFor(total, cat);
  const site = siteMap(cat);
  return specsFor(total, cat).map((sp, i) => place({ key: sp.key, type: sp.type, label: sp.title, base_text: sp.description || "", transfer_hours: sp.transfer_hours || 0, defaults: sp.defaults, options: sp.options,
    change: sp.change, note: "", items: mode === "own" ? [] : sp.defaults.filter((id) => site[id]).map(siteItem) }, i + 1, total, t));
}

export const customized = (d, mode) => {
  if (!d) return false;
  const base = mode === "own" ? [] : d.defaults;
  return !!d.note || d.items.length !== base.length || d.items.some((x, i) => x.type !== "site" || x.site_id !== base[i] || x.request || x.priority);
};

export function reorderDays(days, i, dir, cat) {
  const a = [...days];
  [a[i], a[i + dir]] = [a[i + dir], a[i]];
  const t = templateFor(a.length, cat);
  return a.map((d, j) => place(d, j + 1, a.length, t));
}

export function switchDays(state, total, cat) {
  const t = templateFor(total, cat);
  const byKey = Object.fromEntries(state.days.map((d) => [d.key, d]));
  return buildDays(total, cat, state.mode).map((d) => (byKey[d.key] ? place({ ...byKey[d.key] }, d.n, total, t) : d));
}

export const cost = (s, est) => s.visit_hours + (s.kind === "activity" ? 0 : est.move_hours) + (s.detour_hours || 0);

export function load(d, items, site, est, pace) {
  const sel = items.filter((x) => x.type === "site" && !x.request && site[x.site_id]).map((x) => site[x.site_id]);
  if (d.type === "travel") return { used: 0, cap: 0, left: 0, sel };
  const used = sel.reduce((a, s) => a + cost(s, est), 0) + est.lunch_hours + (d.transfer_hours || 0);
  const cap = est.touring_hours * (est.pace_factor?.[pace] ?? 1);
  return { used, cap, left: cap - used, sel };
}
export const fits = (s, l, est) => cost(s, est) <= l.left + 1e-6;

const join = (a) => (a.length < 2 ? a.join("") : `${a.slice(0, -1).join(", ")} and ${a[a.length - 1]}`);
export function describe(d, site) {
  if (d.type === "travel" || !d.defaults) return d.label;
  const names = d.items.filter((x) => x.type === "site" && !x.request && site[x.site_id]).map((x) => site[x.site_id].name + (site[x.site_id].fee ? "*" : ""));
  return `${d.base_text ? `${d.base_text} ` : ""}${names.length ? `${d.type === "resort" ? "Planned" : "Visit"}: ${join(names)}.` : "No sites selected yet; our team can suggest options for this day."}`;
}

export function evaluate(state, cat) {
  const est = cat.estimates;
  const site = siteMap(cat);
  const p = state.prefs;
  const warnings = [];
  const perDay = state.days.map((d) => {
    const w = [];
    if (d.type === "travel") return { status: "Travel day", hours: 0, cap: 0, w };
    const l = load(d, d.items, site, est, p.pace);
    const over = l.used > l.cap + 1e-6;
    let status = l.used / l.cap <= 0.85 ? "Comfortable" : over ? "Needs Review" : "Busy";
    if (over) w.push({ level: "warn", text: `Day ${d.n} needs about ${l.used.toFixed(1)} hours (sites, transfers and a lunch break) against roughly ${l.cap.toFixed(1)} touring hours at a ${p.pace} pace.` });
    if (l.sel.filter((s) => s.kind !== "activity").length > est.max_sites) { w.push({ level: "warn", text: `Day ${d.n} has more than ${est.max_sites} sites, which is rarely realistic.` }); if (status === "Comfortable") status = "Busy"; }
    d.items.filter((x) => x.request && site[x.site_id]).forEach((x) => w.push({ level: "info", text: `${site[x.site_id].name} is recorded on Day ${d.n} as an over-limit request for our team to review.` }));
    return { status, hours: l.used, cap: l.cap, w };
  });
  perDay.forEach((x, i) => x.w.forEach((y) => warnings.push({ ...y, day: state.days[i].n })));
  const seen = {};
  state.days.forEach((d) => d.items.forEach((x) => {
    if (x.type !== "site" || !site[x.site_id]) return;
    if (seen[x.site_id]) warnings.push({ level: "warn", day: d.n, text: `${site[x.site_id].name} appears on Day ${seen[x.site_id]} and Day ${d.n}.` });
    seen[x.site_id] = seen[x.site_id] || d.n;
  }));
  const all = Object.keys(seen).map((k) => site[k]);
  if (all.some((s) => s.flags.includes("west_bank"))) warnings.push({ level: "info", text: "Bethlehem, Jericho and some other selections are in the West Bank. Our team will coordinate access, transport and guides." });
  all.filter((s) => s.status === "requires_review").forEach((s) => warnings.push({ level: "info", text: `${s.name}: ${s.reservation_notes || "availability needs team review."}` }));
  if (all.some((s) => s.fee)) warnings.push({ level: "info", text: FEE_NOTE });
  if (state.days.some((d) => d.type === "resort")) warnings.push({ level: "info", text: "Dead Sea resort facilities, floating access, treatments and group activities remain subject to confirmation." });
  if (state.days.length === 11) warnings.push({ level: "info", routing: true, text: ROUTING_NOTE });
  if (!p.departure || p.flexible) warnings.push({ level: "info", text: "Opening days and hours will be checked during team review once dates are known." });
  return { perDay, warnings };
}
